import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import DOMPurify from 'dompurify';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  BLOG_SPORT,
  BLOG_SPORT_CATEGORY_CATEGORY,
  BLOG_SPORT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { Excerpt } from '#src/components/Excerpt/index.tsx';
import { DEFAULT_POST_PICTURE } from '#src/constants/types.ts';
import { useDisplayCategories } from '#src/hooks/useDisplayCategories.ts';
import { getPostCategory } from '#src/lib/postCategory.ts';

import type { HeroPost } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({
  gap: 6,
})`
  @media (${laptopAndAbove}) {
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: calc(var(--unit) * 5);
  }
`;

const HeroSidePost = styled(LinkOverlay)`
  display: flex;
  flex-direction: column;
  align-items: center;

  & figure {
    img {
      border-radius: var(--unit);
      aspect-ratio: 1.8;
      object-fit: cover;
      @media (${laptopAndAbove}) {
        width: 50%;
      }
    }
  }

  @media (${laptopAndAbove}) {
    flex-direction: row;
    gap: var(--unit);
    width: calc(var(--unit) * 45);
  }
`;

const StyledChip = styled(Chip)`
  margin-bottom: var(--unit);
`;

const Subtitle = styled(LabelM)`
  color: var(--c-nd-600);
  margin-bottom: var(--unit);
`;

const StyledSideExcerptBody = styled(BodyM)`
  @media (${desktopAndAbove}) {
    & > p {
      font: var(--t-label-s);
    }
  }
`;

const SidePost = ({ excerpt }: { excerpt: HeroPost }) => {
  const { categoryDisplayName } = useDisplayCategories({
    categories: excerpt.categories,
  });
  const { formatDistanceToNow } = useIntlContext();
  const excerptSport = excerpt?.categories?.nodes[0].name;
  const sportCategory = getPostCategory(excerpt);

  const timeAgo = excerpt.date
    ? formatDistanceToNow(excerpt.date, { addSuffix: true })
    : '';

  return (
    <LinkBox>
      <HeroSidePost
        as={Link}
        to={generatePath(BLOG_SPORT_SLUG, {
          sport: sportCategory,
          slug: excerpt.slug,
        })}
      >
        <figure>
          <img
            src={excerpt.featuredImage?.node.thumbnail || DEFAULT_POST_PICTURE}
            alt={excerpt.featuredImage?.node.altText || ''}
          />
        </figure>
      </HeroSidePost>
      <Vertical>
        <LinkOther
          as={Link}
          to={generatePath(BLOG_SPORT_CATEGORY_CATEGORY, {
            sport: sportCategory,
            category: categoryDisplayName.toLowerCase(),
          })}
        >
          <StyledChip color="transparent" size="medium">
            {categoryDisplayName}
          </StyledChip>
        </LinkOther>
        <BrandM as="h4">{excerpt.title}</BrandM>
        <LinkOther
          as={Link}
          to={generatePath(BLOG_SPORT, {
            sport: excerptSport?.toLowerCase() ?? '',
          })}
        >
          <Subtitle>
            {excerptSport} - {timeAgo}
          </Subtitle>
        </LinkOther>
        <StyledSideExcerptBody
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(excerpt.excerpt!),
          }}
        />
      </Vertical>
    </LinkBox>
  );
};

type Props = {
  excerpts: HeroPost[];
};
export const Hero = ({ excerpts }: Props) => {
  return (
    <Root>
      {!!excerpts[0] && <Excerpt excerpt={excerpts[0]} featured />}
      <Vertical gap={6}>
        {excerpts.slice(1, 3).map(excerpt => (
          <SidePost key={excerpt.databaseId} excerpt={excerpt} />
        ))}
      </Vertical>
    </Root>
  );
};

Hero.fragment = gql`
  fragment HeroPost on Post {
    ...getPostCategory_Post
    ...PostExcerpt
  }
  ${getPostCategory.fragments.post}
  ${Excerpt.fragment}
` as TypedDocumentNode<HeroPost>;
